/**
 * DEC-001: Custom Field Bucket Component
 * 
 * Renders fields that aren't mapped in Experience View sections.
 * Ensures new/custom fields from ERPNext are always visible.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FieldRenderer } from '@/features/inventory/item-detail/components/FieldRenderer';
import type { DocFieldSchema } from '@/lib/erp/meta';
import type { CustomFieldBucketConfig } from '@/lib/views/types';
import { useFieldDependencies } from '@/hooks/useFieldDependencies';

interface CustomFieldBucketProps {
  fields: DocFieldSchema[];
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  config?: CustomFieldBucketConfig;
  resetKey?: string | number;
}

export function CustomFieldBucket({
  fields,
  formData,
  onFieldChange,
  disabled,
  config,
  resetKey,
}: CustomFieldBucketProps) {
  const [expanded, setExpanded] = useState(
    config?.visibilityDefault !== 'collapsed'
  );

  const { visibleFieldNames, fieldReadOnly, fieldRequired } = useFieldDependencies(
    fields,
    formData
  );

  const visibleFields = fields.filter(f => visibleFieldNames.has(f.fieldname));

  if (!config?.enabled || visibleFields.length === 0) {
    return null;
  }

  return (
    <Card className="rounded-2xl border-0">
      <CardHeader
        className="cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">
            {config.sectionTitle || 'Additional ERP Fields'}
            {visibleFields.length > 0 && (
              <span className="ml-2 text-muted-foreground font-normal">
                ({visibleFields.length})
              </span>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {visibleFields.map((field) => (
            <FieldRenderer
              key={field.fieldname}
              field={field}
              value={formData[field.fieldname]}
              onChange={(value) => onFieldChange(field.fieldname, value)}
              disabled={disabled}
              isReadOnly={fieldReadOnly[field.fieldname]}
              isRequired={fieldRequired[field.fieldname]}
              resetKey={resetKey}
            />
          ))}
        </CardContent>
      )}
    </Card>
  );
}

